import { PaginatedTable, IPagedResultsV1, Column, Tooltip, TableCellExtras } from '@gcl/react-core';
import * as React from 'react';
import { TableContentLoader } from '@common/TableContentLoader';
import {
  createTableDateTimeColumn,
  createTableDefaultColumn,
  createTableLinkColumn,
} from '@common/tableColumn.services';
import { TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH, DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';

import { IProjectTransmittal } from '@transmittal/transmittal.models';
import { useGetAllTransmittalPurposeOption } from '@transmittal/transmittalPurpose/transmittalPurpose.queries';
import { useNavigate } from '@routes/routes.hooks';
import { getTransmittalItemRoute } from '@transmittal/detailPage/transmittalDetailPage.hooks';
import { TransmittalHistoryTableLayout } from '../TransmittalHistoryTableLayout';
import {
  LatestTransmittalRevisionList,
  ProjectTransmittalHistoryItemPlaceHolder,
} from './transmittalItem/LatestTransmittalRevisionList';
import './TransmittalRefHistory.scss';
import { IProjectTransmittalHistory } from '../transmittalHistory.models';

export interface ITransmittalRefHistoryProps {
  firstTransmittalHistory: IProjectTransmittalHistory | undefined;
  transmittalHistoryPagedResult: IPagedResultsV1<IProjectTransmittalHistory> | undefined;
  onPageChanged: (pageNumber: number, pageSize: number) => void;
  isLoading: boolean;
  hoverTransmittalId: number | undefined;
  setHoverTransmittalId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export const TransmittalRefHistory: React.FunctionComponent<ITransmittalRefHistoryProps> = ({
  firstTransmittalHistory,
  transmittalHistoryPagedResult,
  onPageChanged,
  isLoading = true,
  hoverTransmittalId,
  setHoverTransmittalId,
}) => {
  const { navigate } = useNavigate();
  const numberRegExp = /\d+/g;
  const transmittalNoStartPadding = React.useMemo(() => {
    if (!firstTransmittalHistory || !firstTransmittalHistory.transmittalNo) return '';
    const numbersInTranNo = firstTransmittalHistory.transmittalNo.match(numberRegExp);
    if (numbersInTranNo && numbersInTranNo.length > 0)
      return firstTransmittalHistory.transmittalNo.substring(
        0,
        firstTransmittalHistory.transmittalNo.lastIndexOf(numbersInTranNo[numbersInTranNo.length - 1]),
      );
    return '';
  }, [firstTransmittalHistory]);

  const { data: transmittalPurposeOptions } = useGetAllTransmittalPurposeOption({
    projectId: firstTransmittalHistory?.projectId || 0,
    masterScheduleId: firstTransmittalHistory?.masterScheduleId,
    transmittalType: firstTransmittalHistory?.type,
  });

  const columns: Column[] = React.useMemo(() => {
    return [
      createTableLinkColumn(
        'transmittalNo',
        () => (
          <Tooltip overlay="Click to see transmittal details" id="Click to see transmittal details">
            <div className="gcl-gems-transmittal-ref-history-column-header">
              Transmittal No. ({transmittalNoStartPadding})
            </div>
          </Tooltip>
        ),
        (item: object) => {
          const tempTransmittalDetails = item as IProjectTransmittal;
          return {
            onClick: () => {
              navigate(getTransmittalItemRoute(tempTransmittalDetails.type), {
                id: tempTransmittalDetails.id.toString(),
              });
            },
            displayText: (tempTransmittalDetails.transmittalNo ?? '').startsWith(transmittalNoStartPadding)
              ? tempTransmittalDetails.transmittalNo.substring(transmittalNoStartPadding.length)
              : tempTransmittalDetails.transmittalNo || '',
          };
        },
        true,
      ),
      createTableDateTimeColumn('sendDate', 'Issue Date', 'dd MMM yy', undefined, true, true),
      createTableDefaultColumn(
        'purposeCode',
        () => (
          <Tooltip
            className="gcl-gems-transmittal-ref-history-column-header-tooltip"
            id="gcl-transmittal-ref-history-purpose-code"
            overlay={
              <div className="gcl-gems-transmittal-ref-history-column-header-tooltip">
                <div>
                  <b>Purpose Code for Issue:</b>
                </div>
                <ul>
                  {transmittalPurposeOptions?.map((purpose) => (
                    <li key={purpose.code}>{`${purpose.code} - ${purpose.name}`}</li>
                  ))}
                </ul>
              </div>
            }
          >
            <div className="gcl-gems-transmittal-ref-history-column-header">Issue Purpose</div>
          </Tooltip>
        ),
        undefined,
        'gcl-txt-of-l1',
        undefined,
        true,
        true,
      ),
    ];
  }, [JSON.stringify(transmittalPurposeOptions), JSON.stringify(transmittalNoStartPadding)]);

  return (
    <TableContentLoader isLoading={isLoading}>
      <TransmittalHistoryTableLayout
        className="gcl-mb-md"
        mainTable={
          <div
            className="gcl-gems-transmittal-ref-history-table"
            onMouseLeave={() => {
              setHoverTransmittalId(undefined);
            }}
          >
            <PaginatedTable
              horizontalTableProps={{
                column: {
                  width: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,
                  maxWidth: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,
                  minWidth: TRANSMITTAL_ITEM_REVISION_COLUMN_WIDTH,
                },
              }}
              hoverHighlight={false}
              isHorizontal
              columns={columns}
              items={transmittalHistoryPagedResult?.items || []}
              paginationProps={{
                pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
                paginatorPosition: 'top',
                initialPageSize: transmittalHistoryPagedResult?.pageSize || 0,
                totalCount: transmittalHistoryPagedResult?.totalCount || 0,
                onPageChange: (pageIndex, pageSize) => {
                  onPageChanged(pageIndex + 1, pageSize);
                },
              }}
              allowExpandRow={false}
              allowSelectRow={false}
              responsive={false}
              classNames={{
                dataCell: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                  const transmittalHistory = item as IProjectTransmittalHistory;
                  if (transmittalHistory && transmittalHistory.id === cellExtras?.hoverTransmittalId)
                    return 'gcl-gems-transmittal-history-page-table-cell-highlight';
                  return '';
                },
              }}
              eventProps={{
                dataCell: {
                  onMouseEnter: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                    const transmittalHistory = item as IProjectTransmittalHistory;
                    if (transmittalHistory) cellExtras?.setHoverTransmittalId(transmittalHistory.id);
                  },
                },
              }}
              cellExtras={{ hoverTransmittalId, setHoverTransmittalId }}
            />
          </div>
        }
        pkgTable={
          <LatestTransmittalRevisionList
            transmittalHistoryItems={[
              ProjectTransmittalHistoryItemPlaceHolder,
              ProjectTransmittalHistoryItemPlaceHolder,
              ProjectTransmittalHistoryItemPlaceHolder,
            ]}
          />
        }
      />
    </TableContentLoader>
  );
};
